import React, { useState, useCallback, useEffect } from 'react';
import {
  generatePairingCredentials,
  postPairingIntent,
  type PairDesktopRequest,
} from '../services/desktopPairService';
import {
  fetchPulseApiKey,
  describeExpiry,
  type PulseApiKey,
} from '../services/pulseApiKeyService';

interface DesktopPairModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'color-mix(in srgb, var(--void) 70%, transparent)',
  backdropFilter: 'blur(4px)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--elev)',
  border: '1px solid var(--hair)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--lift-3)',
  padding: 24,
  maxWidth: 420,
  width: '90%',
  color: 'var(--txt)',
  fontFamily: 'var(--font-ui)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  marginBottom: 8,
  color: 'var(--txt)',
};

const descStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: 'var(--muted)',
  marginBottom: 24,
};

const codeDisplayStyle: React.CSSProperties = {
  fontSize: 42,
  fontWeight: 700,
  letterSpacing: 8,
  textAlign: 'center',
  color: 'var(--acc)',
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
  marginBottom: 8,
};

const timerStyle: React.CSSProperties = {
  fontSize: 12,
  textAlign: 'center',
  color: 'var(--muted)',
  fontVariantNumeric: 'tabular-nums',
  marginBottom: 24,
};

const btnRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
};

const btnStyle = (primary = false): React.CSSProperties => ({
  padding: '0 14px',
  height: 32,
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 'var(--radius-sm)',
  border: primary ? '1px solid var(--acc)' : '1px solid var(--border)',
  background: primary ? 'var(--acc)' : 'var(--elev2)',
  color: primary ? 'var(--void)' : 'var(--txt)',
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
  transition: 'background var(--dur-control) var(--ease), border-color var(--dur-control) var(--ease)',
});

const statusStyle = (error: boolean): React.CSSProperties => ({
  fontSize: 12,
  textAlign: 'center',
  marginBottom: 16,
  color: error ? 'var(--down)' : 'var(--muted)',
  minHeight: 20,
});

const keySectionStyle: React.CSSProperties = {
  marginTop: 24,
  paddingTop: 16,
  borderTop: '1px solid var(--hair)',
};

const keyLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--muted-2)',
  marginBottom: 8,
};

const keyBoxStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  lineHeight: 1.5,
  wordBreak: 'break-all',
  color: 'var(--txt-2)',
  background: 'var(--void)',
  border: '1px solid var(--hair)',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 12px',
  maxHeight: 96,
  overflowY: 'auto',
};

const keyNoteStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--muted)',
  marginTop: 8,
};

const DesktopPairModal: React.FC<DesktopPairModalProps> = ({ isOpen, onClose }) => {
  const [credentials, setCredentials] = useState<PairDesktopRequest | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(300);
  const [status, setStatus] = useState('');
  const [isError, setIsError] = useState(false);
  const [posted, setPosted] = useState(false);
  const [apiKey, setApiKey] = useState<PulseApiKey | null>(null);
  const [apiKeyError, setApiKeyError] = useState('');
  const [copied, setCopied] = useState(false);

  const startNewPairing = useCallback(() => {
    const creds = generatePairingCredentials();
    setCredentials(creds);
    setSecondsLeft(300);
    setStatus('Posting pairing intent to server…');
    setIsError(false);
    setPosted(false);

    postPairingIntent(creds)
      .then(() => {
        setStatus('Code is active. Enter it in the desktop app.');
        setPosted(true);
      })
      .catch((err: Error) => {
        setStatus(err.message);
        setIsError(true);
      });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setCredentials(null);
      return;
    }
    startNewPairing();
  }, [isOpen, startNewPairing]);

  useEffect(() => {
    if (!isOpen) {
      setApiKey(null);
      setApiKeyError('');
      setCopied(false);
      return;
    }
    const controller = new AbortController();
    fetchPulseApiKey(controller.signal)
      .then(setApiKey)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setApiKeyError(err.message);
      });
    return () => controller.abort();
  }, [isOpen]);

  const copyApiKey = useCallback(() => {
    if (!apiKey) return;
    navigator.clipboard
      .writeText(apiKey.apiKey)
      .then(() => setCopied(true))
      .catch(() => setApiKeyError('Could not copy — select the key and copy it manually.'));
  }, [apiKey]);

  useEffect(() => {
    if (!isOpen || secondsLeft <= 0 || !posted) return;
    const timer = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setStatus('Code expired. Click Refresh to generate a new one.');
          setIsError(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen, secondsLeft, posted]);

  if (!isOpen) return null;

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Pair Desktop App</h2>
        <p style={descStyle}>
          Open the MMEC Scanner desktop app and enter the code below to link your
          session securely. No credentials are stored in the desktop app.
        </p>

        <div style={codeDisplayStyle}>
          {credentials ? credentials.code : '------'}
        </div>

        <div style={timerStyle}>
          {posted ? `Expires in ${formatTime(secondsLeft)}` : 'Generating…'}
        </div>

        <div style={statusStyle(isError)}>{status}</div>

        <div style={keySectionStyle}>
          <div style={keyLabelStyle}>Elite Pulse API key</div>
          {apiKey ? (
            <>
              <div style={keyBoxStyle}>{apiKey.apiKey}</div>
              <div style={keyNoteStyle}>
                {describeExpiry(apiKey.expiresUtc)} Collect a new one from here when it
                expires.
              </div>
            </>
          ) : (
            <div style={keyNoteStyle}>
              {apiKeyError || 'Issuing your key…'}
            </div>
          )}
        </div>

        <div style={btnRowStyle}>
          <button
            type="button"
            style={btnStyle(false)}
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            style={btnStyle(false)}
            onClick={copyApiKey}
            disabled={!apiKey}
          >
            {copied ? 'Copied' : 'Copy API key'}
          </button>
          <button
            type="button"
            style={btnStyle(true)}
            onClick={startNewPairing}
            disabled={!posted && !isError}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesktopPairModal;
